import { action, computed, observable, override } from 'mobx';
import uniq from 'lodash/uniq';
import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';
import every from 'lodash/every';
import isArray from 'lodash/isArray';
import intersection from 'lodash/intersection';
import isEmpty from 'lodash/isEmpty';
import uniqBy from 'lodash/uniqBy';

import {
    DiagnosisData,
    MedicalCaseData,
    MedicalCaseQuery,
    MedicalCaseVersionData,
    Optional,
    OutwardReferralData,
    OutwardReferralExecutionChangingParams,
    OutwardReferralUpdatingParams,
    RecommendationData,
    RecommendationUpdateParams,
    TaskStatisticData,
} from 'chaika-frontend-types/frontend-cms';
import { MedicalCaseStatus, MedicalCaseTaskState } from 'chaika-enums';

import { LongList } from '@store/abstract/LongList';
import { DateHelper } from '@utils/DateHelper';

import { DiagnosisUpdateIcdParams, DiagnosisUpdateParams, MedicalCase } from './MedicalCase';

export interface MedicalCaseFilterParams {
    excludeIds?: string[];
    ehrId?: string | string[];
    entryId?: string | string[];
    managerId?: string[];
    createdAtFrom?: string;
    outwardReferralId?: string;
    createdAtTo?: string;
    status?: MedicalCaseStatus | MedicalCaseStatus[];
    referralId?: string;
    referralIds?: string[];
    relatedEntryIds?: string[];
    userId?: string;
    nameLike?: string;
    taskState?: MedicalCaseTaskState[];
    hasFollowup?: boolean;
    hasFutureReservations?: boolean;
    closeDataEntryId?: string;
    closeDataDoctorId?: string;
}

export class MedicalCaseList extends LongList<MedicalCase, MedicalCaseData, MedicalCaseFilterParams> {
    public static isClosed(status: MedicalCaseStatus): boolean {
        return status === MedicalCaseStatus.Closed;
    }

    public static getMainVersions(params: MedicalCaseVersionData[]): MedicalCaseVersionData[] {
        const grouped = groupBy(params, 'id');
        const medicalCases: MedicalCaseVersionData[] = [];

        Object.keys(grouped).forEach(id => {
            const [main] = orderBy(grouped[id], 'versionDate', 'desc');
            medicalCases.push(main);
        });

        return medicalCases;
    }

    public override readonly limit = 30;
    @observable public additionalQuery: MedicalCaseQuery = {};

    @computed
    public get patientIds(): string[] {
        return uniq(this.values.map(({ ehrId }) => ehrId));
    }

    @computed
    public get managerIds(): string[] {
        return uniq(this.values.map(({ dto: { managerId } }) => managerId));
    }

    @computed
    public get drugsIds(): string[] {
        return uniq(this.values.flatMap(({ dto: { drugIds } }) => drugIds));
    }

    @computed
    public get referralIds(): string[] {
        return uniq(this.values.flatMap(({ dto: { referralIds } }) => referralIds));
    }

    @computed
    public get hasDiagnoses(): boolean {
        return this.values.flatMap(({ diagnoses }) => diagnoses.map(diagnosis => diagnosis.icdId)).length > 0;
    }

    @computed
    public get everyMedicalCaseHasDiagnoses(): boolean {
        return every([...this.values.map(({ diagnoses }) => diagnoses.length > 0)], it => it);
    }

    @computed
    public get isAllCardsExpanded(): boolean {
        return this.values.every(({ isCardExpanded }) => !!isCardExpanded);
    }

    @computed
    public get recommendations(): RecommendationData[] {
        return uniqBy(
            this.values.flatMap(medicalCase => medicalCase.recommendationList.values),
            'id',
        );
    }

    @action
    public setWithVersions(params: MedicalCaseVersionData[]): void {
        const grouped = groupBy(params, 'id');
        Object.keys(grouped).forEach(id => {
            const [main, ...versions] = orderBy(grouped[id], item => new Date(item.versionDate), 'desc');
            if (!this.has(id)) {
                this.add([main]);
            } else {
                this.update(id, {
                    versionDate: main.versionDate,
                    versionAuthorId: main.versionAuthorId,
                    versionEntryId: main.versionEntryId,
                });
            }
            this.setVersions(id, versions || []);
        });
    }

    @action
    public setVersions(medicalCaseId: string, versions: MedicalCaseVersionData[]): void {
        this.getExisting(medicalCaseId).setVersions(versions);
    }

    @action
    public addVersion(medicalCaseId: string, version: MedicalCaseVersionData): void {
        this.getExisting(medicalCaseId).addVersion(version);
    }

    public getActiveMedicalCaseIds(): string[] {
        return this.getActiveMedicalCases().map(({ id }) => id);
    }

    public getActiveMedicalCases(): MedicalCase[] {
        return this.values.filter(({ status }) => status !== MedicalCaseStatus.Closed);
    }

    public getDiagnosisIcdIds(medicalCaseId: string): string[] {
        return this.getExisting(medicalCaseId).diagnosisIcdIds;
    }

    public getAllDiagnoses(): DiagnosisData[] {
        return this.values.flatMap(medicalCase => medicalCase.getAllDiagnoses());
    }

    public getAllOutwardReferrals(): OutwardReferralData[] {
        return this.values.flatMap(medicalCase => medicalCase.getAllOutwardReferral());
    }

    public getOutwardReferral(id: string): Optional<OutwardReferralData> {
        let outwardReferral;
        this.values.some(medicalCase => {
            const referral = medicalCase.outwardReferralList.get(id);
            if (referral) {
                outwardReferral = referral;
                return true;
            }
            return false;
        });

        return outwardReferral;
    }

    public getDiagnoses(medicalCaseId: string, id: string): Optional<DiagnosisData> {
        return this.get(medicalCaseId)?.getDiagnoses(id);
    }

    @override
    public update(id: string, value: Partial<MedicalCaseData>) {
        this.getExisting(id).update(value);
    }

    @action
    public updateCloseData(id: string, closeData: MedicalCaseData['closeData']) {
        this.getExisting(id).updateCloseData(closeData);
    }

    @action
    public addDiagnosis(medicalCaseId: string, diagnosis: DiagnosisData): void {
        this.get(medicalCaseId)?.addDiagnosis(diagnosis);
    }

    @action
    public addReferral(medicalCaseId: string, referralId: string): void {
        this.get(medicalCaseId)?.addReferral(referralId);
    }

    @action
    public addReferrals(medicalCaseId: string, referralIds: string[]): void {
        this.get(medicalCaseId)?.addReferrals(referralIds);
    }

    @action
    public removeReferral(medicalCaseId: string, referralId: string): void {
        this.get(medicalCaseId)?.removeReferral(referralId);
    }

    @action
    public updateDiagnosis(medicalCaseId: string, params: DiagnosisUpdateParams): void {
        this.get(medicalCaseId)?.updateDiagnosis(params);
    }

    @action
    public updateDiagnosisIcd(medicalCaseId: string, params: DiagnosisUpdateIcdParams): void {
        this.get(medicalCaseId)?.updateDiagnosisIcd(params);
    }

    @action
    public removeDiagnosis(medicalCaseId: string, icdId: string): void {
        this.get(medicalCaseId)?.removeDiagnosis(icdId);
    }

    @action
    public setRecommendations(medicalCaseId: string, recommendations: RecommendationData[]): void {
        this.get(medicalCaseId)?.setRecommendations(recommendations);
    }

    @action
    public addRecommendation(medicalCaseId: string, recommendation: RecommendationData): void {
        this.get(medicalCaseId)?.addRecommendation(recommendation);
    }

    @action
    public updateRecommendation(
        medicalCaseId: string,
        recommendationId: string,
        recommendation: RecommendationUpdateParams & { isImportant?: boolean },
    ): void {
        this.getExisting(medicalCaseId).updateRecommendation(recommendationId, recommendation);
    }

    @action
    public updateRecommendationFromMedicalCases(
        medicalCaseIds: string[],
        recommendationId: string,
        recommendation: RecommendationUpdateParams,
    ): void {
        medicalCaseIds.forEach(medicalCaseId => {
            this.getExisting(medicalCaseId).updateRecommendation(recommendationId, recommendation);
        });
    }

    @action
    public removeRecommendation(medicalCaseId: string, recommendationId: string): void {
        this.getExisting(medicalCaseId).removeRecommendation(recommendationId);
    }

    @action
    public removeRecommendationFromCases(medicalCaseIds: string[], recommendationId: string): void {
        medicalCaseIds.forEach(medicalCaseId => {
            this.get(medicalCaseId)?.removeRecommendation(recommendationId);
        });
    }

    @action
    public setOutwardReferrals(medicalCaseId: string, outwardReferrals: OutwardReferralData[]): void {
        this.get(medicalCaseId)?.setOutwardReferrals(outwardReferrals);
    }

    @action
    public addOutwardReferral(medicalCaseId: string, outwardReferral: OutwardReferralData): void {
        this.get(medicalCaseId)?.addOutwardReferral(outwardReferral);
    }

    @action
    public updateOutwardReferral(
        outwardReferralId: string,
        outwardReferral: OutwardReferralUpdatingParams & Partial<OutwardReferralExecutionChangingParams>,
    ): void {
        const medicalCaseIds = this.getFilteredValuesIds({ outwardReferralId });
        medicalCaseIds.forEach(medicalCaseId => {
            this.getExisting(medicalCaseId).updateOutwardReferral(outwardReferralId, outwardReferral);
        });
    }

    @action
    public updateOutwardReferralExecuted(
        outwardReferralId: string,
        outwardReferral: OutwardReferralExecutionChangingParams,
    ): void {
        const medicalCaseIds = this.getFilteredValuesIds({ outwardReferralId });
        medicalCaseIds.forEach(medicalCaseId => {
            this.getExisting(medicalCaseId).updateOutwardReferralExecuted(outwardReferralId, outwardReferral);
        });
    }

    @action
    public removeOutwardReferral(outwardReferralId: string): void {
        const medicalCaseIds = this.getFilteredValuesIds({ outwardReferralId });
        medicalCaseIds.forEach(medicalCaseId => {
            this.getExisting(medicalCaseId).removeOutwardReferral(outwardReferralId);
        });
    }

    @action.bound
    public expandAllCards(): void {
        this.values.forEach(value => (value.isCardExpanded = true));
    }

    @action.bound
    public collapseAllCards(): void {
        this.values.forEach(value => (value.isCardExpanded = false));
    }

    public getRecommendation(recommendationId: string): Optional<RecommendationData> {
        return this.values
            .find(medicalCase => !!medicalCase.recommendationList.get(recommendationId))
            ?.getRecommendation(recommendationId);
    }

    public getRecommendations(): RecommendationData[] {
        return uniqBy(
            this.values.flatMap(medicalCase => medicalCase.recommendationList.values),
            'id',
        );
    }

    public getMedicalCasesByRecommendationId(recommendationId: string): MedicalCase[] {
        return this.values.filter(medicalCase => medicalCase.recommendationList.has(recommendationId));
    }

    protected getOrderedValues(params: MedicalCase[]): MedicalCase[] {
        return orderBy(params, ['createdAt'], this.orderParams.order);
    }

    protected create(params: MedicalCaseData): MedicalCase {
        return new MedicalCase(params);
    }

    protected filterValue(value: MedicalCase, filterParams: MedicalCaseFilterParams): boolean {
        const filterByExcludeIds: boolean = filterParams.excludeIds
            ? !filterParams.excludeIds.includes(value.id)
            : true;
        const filterByStatus: boolean = filterParams.status
            ? isArray(filterParams.status)
                ? this.filterFieldValueByArray(value, filterParams.status, 'status')
                : value.isOnStatus(filterParams.status)
            : true;
        const filterByEhr: boolean = filterParams.ehrId
            ? isArray(filterParams.ehrId)
                ? filterParams.ehrId.includes(value.dto.ehrId)
                : value.ehrId === filterParams.ehrId
            : true;
        const filterByEntry: boolean = filterParams.entryId
            ? isArray(filterParams.entryId)
                ? (value.dto.parentEntryId !== undefined && filterParams.entryId.includes(value.dto.parentEntryId)) ||
                  !isEmpty(intersection(filterParams.entryId, value.dto.addedEntryIds))
                : value.dto.parentEntryId === filterParams.entryId ||
                  value.dto.addedEntryIds?.includes(filterParams.entryId)
            : true;
        const filterByManager: boolean = filterParams.managerId
            ? filterParams.managerId.includes(value.dto.managerId)
            : true;
        const filterByReferralId: boolean = filterParams.referralId ? value.hasReferral(filterParams.referralId) : true;
        const filterByReferralIds: boolean = filterParams.referralIds
            ? filterParams.referralIds.some(id => value.hasReferral(id))
            : true;
        const filterByCreatedAtFrom: boolean = filterParams.createdAtFrom
            ? DateHelper.isSameOrAfter(value.createdAt, DateHelper.getStartOfDay(filterParams.createdAtFrom))
            : true;
        const filterByCreatedAtTo: boolean = filterParams.createdAtTo
            ? DateHelper.isSameOrBefore(value.createdAt, DateHelper.getEndOfDay(filterParams.createdAtTo))
            : true;
        const filterByUserId: boolean = filterParams.userId
            ? value.isManager(filterParams.userId) || value.isAuthor(filterParams.userId)
            : true;
        const filterByRelatedEntryIds: boolean = !!filterParams.relatedEntryIds
            ? filterParams.relatedEntryIds.some(id => value.relatedToEntry(id))
            : true;
        const filterByNameLike: boolean = !!filterParams.nameLike
            ? value.name.toLowerCase().includes(filterParams.nameLike.toLowerCase())
            : true;
        const filterByTaskState =
            !!filterParams.taskState && value.taskStatistic
                ? this.taskStateFilter(value.taskStatistic, filterParams.taskState)
                : true;
        const filterByOutwardReferralId = !!filterParams.outwardReferralId
            ? value.hasOutwardReferral(filterParams.outwardReferralId)
            : true;
        const filterByCloseDataEntryId = !!filterParams.closeDataEntryId
            ? value.closeData?.entryId === filterParams.closeDataEntryId
            : true;
        const filterByCloseDataDoctorId = !!filterParams.closeDataDoctorId
            ? value.closeData?.doctorId === filterParams.closeDataDoctorId
            : true;

        return (
            filterByStatus &&
            filterByExcludeIds &&
            filterByManager &&
            filterByRelatedEntryIds &&
            filterByCreatedAtFrom &&
            filterByCreatedAtTo &&
            filterByReferralId &&
            filterByReferralIds &&
            filterByUserId &&
            filterByNameLike &&
            filterByEhr &&
            filterByEntry &&
            filterByTaskState &&
            filterByOutwardReferralId &&
            filterByCloseDataEntryId &&
            filterByCloseDataDoctorId
        );
    }

    private taskStateFilter(taskStatistic: TaskStatisticData, taskState: MedicalCaseTaskState[]): boolean {
        const hasActiveFilter = taskState.includes(MedicalCaseTaskState.Active);
        const hasOverDueFilter = taskState.includes(MedicalCaseTaskState.Overdue);
        const hasWithoutActiveFilter = taskState.includes(MedicalCaseTaskState.WithoutActive);

        return (
            (hasActiveFilter && taskStatistic?.activeTaskCount > 0) ||
            (hasOverDueFilter && taskStatistic?.overdueTaskCount > 0) ||
            (hasWithoutActiveFilter && taskStatistic?.activeTaskCount === 0 && taskStatistic?.overdueTaskCount === 0)
        );
    }
}
